// Copyright (c) 2025, Dokos and Contributors
// MIT License. See license.txt

import "./PMTaskAssignmentQuickEntryForm";
import { PMViewCalendar_Assignments } from "./PMViewCalendar_Assignments";
import { PMViewCalendar_Timelog } from "./PMViewCalendar_Timelog";

/** @typedef {"schedule" | "assign" | "log"} pm_mode_t */
export class ProjectManagementView extends frappe.views.ListView {
	constructor(opts) {
		super(opts);
		this._mode = "log";
		this._date = "";
		this.buttons = {};
	}

	get mode() {
		return this._mode;
	}
	set mode(v) {
		this._mode = v || "log";
		this.setActiveCalendar();
	}

	get date() {
		const dateObj = this.calendar?.fullcalendar?.getDate();
		const dateStr = dateObj && moment(dateObj).format("YYYY-MM-DD HH:mm:ss");
		return dateStr?.slice(0, 10) ?? this._date;
	}
	set date(v) {
		this._date = v;
		this.calendar?.fullcalendar?.gotoDate(v);
	}

	get_search_params() {
		const params = super.get_search_params();
		params.append("view", this.mode);
		params.append("date", this.date);
		return params;
	}

	setup_defaults() {
		super.setup_defaults();
		this.page_title = __("Project Management");
	}

	setup_view() {
		// Hide standard sort selectors that don't apply to calendar views
		if (this.sort_selector) {
			this.sort_selector.wrapper[0].classList.add("hidden");
		}
		this.setup_pm_header();
	}

	setup_pm_header() {
		this.custom_header = document.createElement("div");
		this.custom_header.className = "pm-view-header flex flex-between p-3";

		const btn_group = document.createElement("div");
		btn_group.className = "btn-group";

		const modes = [
			{ id: "assign", label: __("Assign", null, "PM View"), icon: "es-line-add-people" },
			{ id: "log", label: __("Log Time", null, "PM View"), icon: "es-line-time" },
		];

		modes.forEach((m) => {
			const btn = document.createElement("button");
			btn.className = "btn btn-default btn-sm";
			btn.innerHTML = `${frappe.utils.icon(m.icon, "sm")} <span class="hidden-xs">${m.label}</span>`;
			btn.addEventListener("click", () => {
				this.mode = m.id;
			});
			btn_group.appendChild(btn);
			this.buttons[m.id] = btn;
		});

		this.custom_header.appendChild(btn_group);

		this.legendContainer = document.createElement("div");
		this.legendContainer.className = "pm-legend-container flex align-center";
		this.custom_header.appendChild(this.legendContainer);

		this.$result[0].parentNode.insertBefore(this.custom_header, this.$result[0]);
	}

	async render() {
		if (this.calendar) {
			this.calendar.refresh();
			return;
		}
		await this.setActiveCalendar();
	}

	async setActiveCalendar() {
		Object.keys(this.buttons).forEach((key) => {
			const btn = this.buttons[key];
			if (key === this.mode) {
				btn.classList.add("btn-primary-light");
				btn.classList.remove("btn-default");
			} else {
				btn.classList.remove("btn-primary-light");
				btn.classList.add("btn-default");
			}
		});

		await this.rebuildCalendar();
	}

	async rebuildCalendar() {
		this.update_url_with_filters();
		if (this.calendar?.destroy) {
			this.calendar.destroy();
		}

		this.legendContainer.innerHTML = "";

		let CalendarClass = this.mode === "assign" ? PMViewCalendar_Assignments : PMViewCalendar_Timelog;

		this.calendar = new CalendarClass({
			page: this.page,
			view: this,
			parent: this.$result,
		});

		if (this.calendar.setupLegend) {
			this.calendar.setupLegend(this.legendContainer);
		}

		await this.calendar.make();

		const btn_label = this.mode === "assign" ? __("Add Assignment") : __("Add Entry");
		this.page.set_primary_action(btn_label, () => {
			this.calendar.eventSources[0].doGuiCreateDocument();
		});
	}
}

frappe.views.registerCustomListView?.(
	"Project Management",
	"pm",
	"es-line-time",
	ProjectManagementView,
	(listViewSelect) => {
		return {
			label: "Project Management",
			condition: ["Project", "Task", "Task Assignment", "Timesheet"].includes(listViewSelect.doctype),
			action: () => listViewSelect.set_route("pm"),
			current_view_handler: () => {},
		};
	}
);
